import { useEffect, useState } from 'react'
import { Download, HardDriveDownload, HelpCircle, Layers3, Monitor, RefreshCw, WifiOff, X } from 'lucide-react'

interface InstallDialogProps {
  open: boolean
  nativeInstallAvailable: boolean
  onClose: () => void
  onInstall: () => Promise<void>
}

export function InstallDialog({ open, nativeInstallAvailable, onClose, onInstall }: InstallDialogProps) {
  const [installing, setInstalling] = useState(false)

  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !installing) onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [installing, onClose, open])

  if (!open) return null
  return (
    <div className="modal-backdrop install-backdrop" onMouseDown={() => !installing && onClose()}>
      <section className="modal install-dialog" role="dialog" aria-modal="true" aria-labelledby="install-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="modal-close" aria-label="Close install dialog" disabled={installing} onClick={onClose}><X size={18} /></button>
        <div className="install-dialog-icon"><Download size={25} /></div>
        <div className="modal-kicker">Desktop app</div>
        <h2 id="install-title">Install Deckelier</h2>
        <p className="modal-intro">Launch Deckelier from your desktop and keep the complete editor ready when you have no connection.</p>
        <div className="install-benefits">
          <div><WifiOff size={18} /><span><strong>Work offline</strong><small>Design, edit data, render, and export without a connection.</small></span></div>
          <div><HardDriveDownload size={18} /><span><strong>Keep control</strong><small>Projects remain in this browser installation unless you save a backup.</small></span></div>
        </div>
        {nativeInstallAvailable
          ? <p className="install-instruction">Your browser is ready to install Deckelier.</p>
          : <p className="install-instruction">Open your browser menu and look for <strong>Install Deckelier</strong> or <strong>Install app</strong>. In Safari, choose <strong>File → Add to Dock</strong>.</p>}
        <p className="install-caveat">Installing does not sync projects between devices. Keep important project backups outside the browser.</p>
        <div className="modal-actions">
          <button className="button ghost" disabled={installing} onClick={onClose}>{nativeInstallAvailable ? 'Not now' : 'Close'}</button>
          {nativeInstallAvailable && <button className="button primary" disabled={installing} onClick={async () => {
            setInstalling(true)
            try {
              await onInstall()
              onClose()
            } finally {
              setInstalling(false)
            }
          }}><Download size={16} /> {installing ? 'Opening installer…' : 'Install'}</button>}
        </div>
      </section>
    </div>
  )
}

interface DesktopRequiredScreenProps {
  projectName: string
  onBack: () => void
  onBackup: () => void
  onHelp: () => void
}

export function DesktopRequiredScreen({ projectName, onBack, onBackup, onHelp }: DesktopRequiredScreenProps) {
  return (
    <main className="desktop-required-page">
      <div className="desktop-required-brand"><span className="brand-mark"><Layers3 size={23} /></span><strong>Deckelier</strong><span className="beta-badge">Beta</span></div>
      <section className="desktop-required-card">
        <div className="desktop-required-icon"><Monitor size={34} /></div>
        <span className="eyebrow plain">Desktop editor</span>
        <h1>The editor needs a larger screen.</h1>
        <p>Deckelier requires at least 1100 pixels of workspace and a mouse or trackpad. <strong>{projectName}</strong> remains saved and has not been changed.</p>
        <div className="desktop-required-actions">
          <button className="button primary" onClick={onBack}>Back to projects</button>
          <button className="button secondary" onClick={onBackup}><HardDriveDownload size={16} /> Save backup</button>
          <button className="button ghost" onClick={onHelp}><HelpCircle size={16} /> Help</button>
        </div>
        <small>If you resized a desktop window, widen it to return to the editor automatically.</small>
      </section>
    </main>
  )
}

interface PwaUpdateNoticeProps {
  available: boolean
  onUpdate: () => void
}

export function PwaUpdateNotice({ available, onUpdate }: PwaUpdateNoticeProps) {
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => {
    if (!available) setDismissed(false)
  }, [available])
  if (!available || dismissed) return null
  return (
    <aside className="pwa-update-notice" role="status">
      <RefreshCw size={18} />
      <span><strong>Deckelier has an update</strong><small>Your work will be saved before the app reloads.</small></span>
      <button className="button secondary compact" onClick={onUpdate}>Update now</button>
      <button className="icon-button" aria-label="Update later" onClick={() => setDismissed(true)}><X size={16} /></button>
    </aside>
  )
}
